import { FileDto } from "./FileDto";
import { GeocodePrecision } from "./Geo";

// #509 (backend): BFF-computed availability state of a catalog listing. Rows other than
// 'available' are index-only — rendered without an order action.
//
// Backend #562 (decision 5): the state formerly called 'availableInStore' is 'availableAtSource'
// — it only ever asserted "the source shop reports this as in stock", never that there is a
// Geschäft, let alone one you may walk into. The transitional acceptance of the old literal
// (F1) is retired here in F4, now that backend #654 ships the new value.
export type CatalogAvailability = 'available' | 'availableAtSource' | 'unavailable';

/**
 * Backend #562: true for the "the seller has it, we cannot sell it" state.
 *
 * Deliberately a positive test on the known literal rather than "not available": an absent or
 * unrecognised state — the retired `availableInStore` included — must fall through to the
 * neutral badge, never to this one, which makes a concrete claim about the seller's stock.
 */
export function isAvailableAtSource(availability: string | null | undefined): boolean {
  return availability === 'availableAtSource';
}

export class CatalogSku {
    skuId: string;
    skuNumber: string;
    skuShortName: string;
    skuName1: string;
    catalogNetPrice: number;
    catalogGrossPrice: number;
    finalPurchaseGrossPrice: number;
    discountPercent: number;
    // Backend #525: uppercase ISO 4217 currency of all the listing's prices — carried, never
    // converted. Default covers a pre-#525 backend, where every price was implicitly EUR.
    currency: string = 'EUR';
    // Backend: the listing's VAT rate in percent, for the price note beside the price. Undefined
    // or null means there is no rate to state — a seller who assigned no VAT, an older backend
    // that does not send the field, or the undetermined sentinel (#579), which the BFF maps to
    // null rather than to its placeholder 0%. `PriceNotePipe` then writes the rate-less note
    // instead of guessing; a real 0% rate exists beside that sentinel, so 0 is a rate.
    vatPercentage?: number | null;
    hasLargeImage: boolean;
    hasSmallImage: boolean;
    smallImageUrl: string;
    largeImageUrl: string;
    // Backend #633 A: the tag set. `productCategoryShortNames` are the stable KEYS (backend
    // #688 — „Buecher", not „Bücher"); `productCategoryNames` are the display names the backend
    // resolved for the language this app asked for. Render the names.
    productCategoryShortNames: string[] = [];
    productCategoryNames?: string[];
    skuDescription: string;
    /**
     * Backend #690: the language THIS listing's own text is written in — `de`, `en`, or absent.
     * Display metadata only: the catalog never filters or sorts by it. **Absent means undeclared
     * and stays undeclared** (a scanned listing whose source shop declares no language), so a
     * client renders no `lang` attribute rather than calling it German.
     */
    skuLanguage?: string | null;
    availableQuantity: number;
    availability: CatalogAvailability;
    sellerFullName: string;
    sellerCustomerId: string;
    files: FileDto[] = [];
    isRetailer: boolean;
    smallBusinessP19UStG: boolean = false;
    // Backend #562: scan-owned display data. `sourceUrl` is the source shop's product page for a
    // listing a scan provider imported from an external shop, `isImported` the origin flag itself.
    // Both absent for a manually created listing — and for a pre-#562 backend, which sends neither.
    sourceUrl?: string;
    isImported?: boolean;
    /**
     * Backend #678 slice C: the seller's position (a listing's position is its seller's), present
     * whenever the seller has a Resolved coordinate — located search or not. A private seller's is
     * a postcode / locality centroid, which `sellerGeocodePrecision` says (draw an area, not a
     * point). `distanceKm` is from the searcher's position, as the crow flies, and only on a
     * located search. **All four null means unknown, never far away.**
     */
    sellerLatitude?: number | null;
    sellerLongitude?: number | null;
    sellerGeocodePrecision?: GeocodePrecision | null;
    distanceKm?: number | null;
}

/**
 * Backend #562: the source shop's product page of an imported listing, but only when the stored
 * value parses as an absolute `http(s)` URL with a host — otherwise null, and the catalog renders
 * no shop link at all rather than a broken one (decisions 1/4: URL presence drives the button).
 *
 * Catalog.Api validates the scheme on write, yet this value is scraped from a third party and ends
 * up as a clickable link, so the client checks it too rather than trusting the wire. A plain
 * function, not a method: catalog listings arrive as parsed JSON, so they are never real
 * `CatalogSku` instances at runtime and would not carry a class getter.
 */
export function catalogSkuShopUrl(catalogSku: Pick<CatalogSku, 'sourceUrl'> | null | undefined): string | null {
  const raw = catalogSku?.sourceUrl?.trim();
  if (!raw) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(raw);
  } catch {
    return null;
  }

  const isHttp = parsed.protocol === 'http:' || parsed.protocol === 'https:';
  return isHttp && !!parsed.host ? raw : null;
}
